import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useResource, formatDate, titleCase } from "../lib/hooks";
import { uploadFile, fileViewUrl } from "../lib/files";
import { IdCardOverlay } from "../components/IdCard";
import { MyDocuments } from "../components/MyDocuments";
import { avatarPair, initials } from "../components/InitialsAvatar";

interface Profile {
    id: string;
    photo_file_id: string | null;
    full_name: string;
    email: string | null;
    employment_type: string;
    start_date: string;
    abn: string | null;
    contact_phone: string | null;
    address: string | null;
    suburb: string | null;
    emergency_contact: string | null;
    emergency_phone: string | null;
    bank_account_name: string | null;
    bank_bsb: string | null;
    bank_account_number: string | null;
    super_fund_name: string | null;
    super_member_number: string | null;
}

const EDITABLE: Array<{ key: keyof Profile; label: string; section: string; placeholder?: string; help?: string }> = [
    { key: "contact_phone", label: "Phone", section: "Contact" },
    { key: "address", label: "Address", section: "Contact" },
    { key: "suburb", label: "Suburb", section: "Contact" },
    { key: "emergency_contact", label: "Emergency contact name", section: "Emergency" },
    { key: "emergency_phone", label: "Emergency contact phone", section: "Emergency" },
    { key: "bank_account_name", label: "Account name", section: "Payment details" },
    { key: "bank_bsb", label: "BSB", section: "Payment details", placeholder: "066-000" },
    { key: "bank_account_number", label: "Account number", section: "Payment details", placeholder: "12345678" },
    { key: "super_fund_name", label: "Super fund", section: "Superannuation" },
    { key: "super_member_number", label: "Member number", section: "Superannuation" },
];

const SECTIONS = ["Contact", "Emergency", "Payment details", "Superannuation"];

/**
 * Self-service password change — the one card on this page that exists for
 * every account, partners included. The API re-checks the current password,
 * so a walked-away-from screen is not enough to take over the account.
 */
const PasswordCard = () => {
    const { signOut } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (next !== confirm) {
            setMessage({ text: "The new passwords do not match.", ok: false });
            return;
        }
        setBusy(true);
        setMessage(null);
        try {
            await api.post("/auth/change-password", { currentPassword: current, newPassword: next });
            setCurrent(""); setNext(""); setConfirm("");
            // A password change ends every session for this account, this one
            // included — sign out deliberately, on a short delay so the
            // message is actually readable, rather than let it surface as an
            // unexplained 401 on whatever's clicked next.
            setMessage({ text: "Password changed. You'll be signed out in a moment — sign in again with your new password.", ok: true });
            setTimeout(() => signOut(), 2000);
        } catch (err) {
            setMessage({ text: err instanceof Error ? err.message : "Could not change the password", ok: false });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head"><h2>Password</h2></div>
            <div className="card-body">
                <form onSubmit={(e) => void submit(e)}>
                    <div className="form-grid">
                        <div className="field">
                            <label htmlFor="pw-current">Current password</label>
                            <input id="pw-current" type="password" autoComplete="current-password"
                                value={current} onChange={(e) => setCurrent(e.target.value)} required />
                        </div>
                        <div className="field half">
                            <label htmlFor="pw-next">New password</label>
                            <input id="pw-next" type="password" autoComplete="new-password"
                                value={next} onChange={(e) => setNext(e.target.value)} required />
                            <span className="help">At least 12 characters, with upper and lower case and a number.</span>
                        </div>
                        <div className="field half">
                            <label htmlFor="pw-confirm">Repeat new password</label>
                            <input id="pw-confirm" type="password" autoComplete="new-password"
                                value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginTop: "0.6rem" }}>
                        <button className="btn" type="submit" disabled={busy || !current || !next || !confirm}>
                            {busy ? "Changing…" : "Change password"}
                        </button>
                        {message && (
                            <span style={{ fontSize: "0.8rem", color: message.ok ? "var(--muted)" : "var(--danger, #b3261e)" }}>
                                {message.text}
                            </span>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

/**
 * The signed-in person's own page, whatever their role. Workers — including
 * managers and supervisors, who are workers too — see and edit their employee
 * record: contact, emergency, bank and super details, exactly the API's
 * allowlist. Accounts with no employee record (partners, some admins) still
 * get the page: their account facts and the password card.
 */
export const MyProfile = () => {
    const { user } = useAuth();
    const isPartner = user?.role === "partner";
    // Partners have no employee record by definition — skip the request
    // rather than provoke a guaranteed 404.
    const profile = useResource<Profile>(isPartner ? null : "/me/profile");
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [showCard, setShowCard] = useState(false);

    const uploadPhoto = async (file: File) => {
        setBusy(true);
        setMessage(null);
        try {
            const stored = await uploadFile(file, "general");
            await api.put("/me/profile", { photo_file_id: stored.id });
            setMessage("Photo updated.");
            profile.reload();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Photo upload failed");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!profile.data) return;
        const next: Record<string, string> = {};
        for (const field of EDITABLE) next[field.key] = (profile.data[field.key] as string | null) ?? "";
        setDraft(next);
    }, [profile.data]);

    const save = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const payload: Record<string, string | null> = {};
            for (const field of EDITABLE) payload[field.key] = draft[field.key]?.trim() || null;
            await api.put("/me/profile", payload);
            setMessage("Saved. Payment detail changes are logged for your protection.");
            profile.reload();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const data = profile.data;
    if (!isPartner && !data && !profile.error) {
        return <p style={{ color: "var(--muted)" }}>Loading…</p>;
    }

    // No employee record behind this account — partner, or an admin/viewer
    // login that was never linked to a worker. The page still stands: account
    // facts plus the password card, with an honest note instead of an error.
    if (isPartner || !data) {
        return (
            <div style={{ display: "grid", gap: "1rem", maxWidth: 780 }}>
                <div className="card">
                    <div className="card-head"><h2>{user?.fullName ?? user?.email}</h2></div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>Email</dt><dd>{user?.email}</dd>
                            <dt>Role</dt><dd>{titleCase(user?.role ?? "")}</dd>
                        </dl>
                        <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                            {isPartner ? (
                                <>Your organisation's details — address, phone and the people TrustCare can
                                contact — live under <Link to="/partner/organisation">My organisation</Link>.</>
                            ) : (
                                "This account is not linked to an employee record, so there are no worker details to show here. An administrator can link one from Settings if you also work shifts."
                            )}
                        </p>
                    </div>
                </div>
                <PasswordCard />
            </div>
        );
    }

    return (
        <div style={{ display: "grid", gap: "1rem", maxWidth: 780 }}>
            {showCard && <IdCardOverlay employeeId={data.id} onClose={() => setShowCard(false)} />}

            <div className="card">
                <div className="card-head">
                    <h2>{data.full_name}</h2>
                    <button className="btn small" onClick={() => setShowCard(true)}>My ID card</button>
                </div>
                <div className="card-body">
                    <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "0.8rem" }}>
                        <div className="profile-photo">
                            {data.photo_file_id
                                ? <img src={fileViewUrl(data.photo_file_id)} alt="Your photo" />
                                : <span style={{ background: avatarPair(data.full_name)[0], color: avatarPair(data.full_name)[1] }}>{initials(data.full_name)}</span>}
                        </div>
                        <div>
                            <label className="btn ghost small" style={{ cursor: "pointer" }}>
                                {data.photo_file_id ? "Change photo" : "Add photo"}
                                <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                                    disabled={busy}
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadPhoto(f); }} />
                            </label>
                            <p style={{ fontSize: "0.74rem", color: "var(--muted)", marginTop: "0.35rem" }}>
                                Shown on your digital ID card. A clear head-and-shoulders photo works best.
                            </p>
                        </div>
                    </div>
                    <dl className="kv">
                        <dt>Employment</dt><dd>{titleCase(data.employment_type)}{data.abn ? ` · ABN ${data.abn}` : ""}</dd>
                        <dt>Started</dt><dd>{formatDate(data.start_date)}</dd>
                        <dt>Email</dt><dd>{data.email ?? "—"}</dd>
                    </dl>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                        Employment terms and pay rates are set by management — ask your manager if
                        something here looks wrong. Your tax file number is never stored in this
                        system; TFN declarations go to payroll directly.
                    </p>
                </div>
            </div>

            {SECTIONS.map((section) => (
                <div key={section} className="card">
                    <div className="card-head"><h2>{section}</h2></div>
                    <div className="card-body">
                        <div className="form-grid">
                            {EDITABLE.filter((f) => f.section === section).map((field) => (
                                <div key={field.key} className="field half">
                                    <label htmlFor={`pf-${field.key}`}>{field.label}</label>
                                    <input
                                        id={`pf-${field.key}`}
                                        value={draft[field.key] ?? ""}
                                        placeholder={field.placeholder}
                                        onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
                                    />
                                    {field.help && <span className="help">{field.help}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}

            <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                <button className="btn" onClick={() => void save()} disabled={busy}>
                    {busy ? "Saving…" : "Save my details"}
                </button>
                {message && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</span>}
            </div>

            <MyDocuments />

            <PasswordCard />
        </div>
    );
};
